import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalizedText } from '../../lib/localizedText'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import { Dropdown } from 'primereact/dropdown'
import { Menu } from 'primereact/menu'
import type { MenuItem } from 'primereact/menuitem'
import Button from '../../components/ui/Button'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import RefreshButton from '../../components/ui/RefreshButton'
import FilterBar from '../../components/ui/FilterBar'
import PageHeader from '../../components/ui/PageHeader'
import { useToast } from '../../components/ui/ToastProvider'
import { isOptimisticLock } from '../../api/apiError'
import { hasContent } from '../../lib/html'
import { notificationTemplateColumns } from './components/NotificationTemplateColumns'
import NotificationTemplateFormDialog from './components/NotificationTemplateFormDialog'
import type { NotificationTemplateFormValues } from './components/notificationTemplateFormSchema'
import {
  exportNotificationTemplates,
  getNotificationTemplate,
} from './api/notificationTemplateApi'
import { useNotificationTemplates } from './hooks/useNotificationTemplates'
import { useTableSort } from '../../lib/useTableSort'
import type { NotificationTemplate, NotificationTemplateUpsert } from './types'
import Badge from '../../components/ui/Badge'
import { InputSwitch } from 'primereact/inputswitch'
import { Paginator, type PaginatorPageChangeEvent } from 'primereact/paginator'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { useProxyMode } from '../proxy/useProxyMode'
import './admin.css'

/** Varsayılan sayfa boyutu (anayasa: tüm listelerde 10). */
const PAGE_SIZE = 10

/**
 * "Tümü" seçeneğinin değeri. Boş-OLMAYAN bir sentinel gerekir: PrimeReact Dropdown, `null`/`''` değerli
 * seçeneği "boş seçim" sayıp `onChange`'i yutar (aynı sorun `OperationCodesAdminPage`'de `ALL_MODULES`
 * ile belgelenmiştir). Negatif değer seçildi çünkü referans kimlikleri daima pozitiftir → gerçek bir
 * kayıtla çakışamaz. API'ye giderken `null`'a (filtresiz) çevrilir.
 */
const ALL = -1

/** Mobil kart kebab menüsü — switch dışındaki Düzenle/Sil'i tek düğmede toplar (v2 madde 1). */
function TemplateCardMenu({
  row,
  busy,
  onEdit,
  onDelete,
  t,
}: Readonly<{
  row: NotificationTemplate
  busy: boolean
  onEdit: (row: NotificationTemplate) => void
  onDelete: (row: NotificationTemplate) => void
  t: (key: string) => string
}>) {
  const menuRef = useRef<Menu>(null)
  const items: MenuItem[] = [
    { label: t('admin.notificationTemplates.actions.edit'), icon: 'pi pi-pencil', command: () => onEdit(row) },
    {
      label: t('admin.notificationTemplates.actions.delete'),
      icon: 'pi pi-trash',
      className: 'ui-card-menu-item--danger',
      command: () => onDelete(row),
    },
  ]
  return (
    <>
      <Menu model={items} popup ref={menuRef} appendTo={document.body} />
      <Button
        icon="pi pi-ellipsis-v"
        rounded
        text
        disabled={busy}
        /* Erişilebilir ad ŞABLONU da söyler — yinelenen kartlarda yalnız "işlemler"/"görüntüle"
           diyen düğmeler ekran okuyucuda ayırt edilemiyordu (THY AI review bulgusu, 26.10.2026).
           Değer kartta görünen konuyla (`subjectTr`) aynı. */
        aria-label={`${row.subjectTr}, ${t('admin.notificationTemplates.actions.menu')}`}
        onClick={(e) => menuRef.current?.toggle(e)}
      />
    </>
  )
}

/**
 * Admin → **Bildirim Şablonu** (059). `REF_NOTIFICATION_TEMPLATE` için tam CRUD ekranı.
 *
 * Filtreler: İşlem Kodu + Bildirim Türü comboları; ikisi **VE** mantığıyla birleşir ve filtre
 * değişiminde sayfa **sıfırlanır** (FR-005). Sayfalama ve **kolon sıralaması sunucu-taraflıdır**
 * (`lazy` + `useTableSort`): istemcide yalnız açık sayfa durduğundan sıralama tüm kayıt kümesine
 * uygulanmalıdır. Elle sıra (↑/↓) **yoktur** — tabloda `SORT_INDEX` yok.
 * Yetki: `NOTIFICATION_TEMPLATE_MANAGE`.
 *
 * Tablo canlıda **boştur** (0 kayıt) → ilk açılışta boş-durum görünür; bu **beklenen** davranıştır.
 *
 * <p>Kısıtlı Yetkili asistan (126) ekranı <b>salt okunur</b> görür: "Yeni ekle" ve Sil çizilmez, durum
 * anahtarı pasif kalır (durum okunabilsin diye gizlenmez). Konu/içerik/alıcı metinleri yalnız pop-up'ta
 * göründüğü için Düzenle'nin yerini <b>Görüntüle</b> alır: kayıt aynı tekil uçtan yüklenir ve pop-up
 * kaydetme düğmesi olmadan, alanlar pasif açılır.
 */
export default function NotificationTemplatesAdminPage() {
  const { t } = useTranslation()
  const localized = useLocalizedText()
  const toast = useToast()
  const { readOnly } = useProxyMode()

  const [operationCodeId, setOperationCodeId] = useState<number>(ALL)
  const [notificationTypeId, setNotificationTypeId] = useState<number>(ALL)
  const [first, setFirst] = useState(0)
  const [rows, setRows] = useState(PAGE_SIZE)
  // Sıralama değişince ilk sayfaya dönülür: eski sayfa numarası yeni düzende başka kayıtlara denk gelir.
  const sorting = useTableSort(() => setFirst(0))

  const { list, operationCodes, notificationTypes, parameters, create, update, changeStatus, remove } =
    useNotificationTemplates({
    operationCodeId: operationCodeId === ALL ? null : operationCodeId,
    notificationTypeId: notificationTypeId === ALL ? null : notificationTypeId,
    page: Math.floor(first / rows),
    size: rows,
    sort: sorting.sort,
  })

  const items = list.data?.content ?? []
  const total = list.data?.page.totalElements ?? 0
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  // Form durumu: `null` = kapalı · `'new'` = yeni kayıt · kayıt = düzenleme.
  // Düzenlemede tekil uç çağrılır çünkü liste yanıtı CLOB alanlarını taşımaz (research D10).
  const [editing, setEditing] = useState<NotificationTemplate | 'new' | null>(null)
  const [loadingRow, setLoadingRow] = useState(false)
  /** Durum/silme isteği süren satır — o satırın kontrolleri kilitlenir. */
  const [busyId, setBusyId] = useState<number | null>(null)

  /**
   * Düzenleme formunu açar. Tekil uç çağrılır çünkü liste yanıtı CLOB alanlarını taşımaz (D10).
   *
   * **Hata YUTULMAZ:** bu çağrı React Query dışındadır, dolayısıyla merkezî `onError` toast'ı (019)
   * devreye girmez. Yakalanmazsa kullanıcı için "butona bastım, hiçbir şey olmadı" (ve konsolda
   * yakalanmamış promise) demek olurdu → burada açıkça toast basılır.
   */
  const openEdit = async (row: NotificationTemplate) => {
    setLoadingRow(true)
    try {
      setEditing(await getNotificationTemplate(row.id))
    } catch {
      toast.show({
        severity: 'error',
        summary: t('common.errors.generic'),
        life: 5000,
      })
    } finally {
      setLoadingRow(false)
    }
  }

  /**
   * Form değerlerini istek gövdesine çevirir; **boş opsiyonel alanlar `null`** olur.
   *
   * <p>EN içerikte boşluk kontrolü `trim()` değil {@link hasContent} ile yapılır: e-posta türünde
   * içerik HTML'dir ve boşaltılmış bir editör `<p><br></p>` gibi **görünür metni olmayan** ama
   * `trim()` sonrası dolu görünen bir değer üretebilir. Öyle bir değeri kaydetmek, "EN içerik var"
   * yanılgısı yaratır ve gönderimde boş bir e-posta gövdesine dönüşür. Düz metin içerikte ise
   * etiket sökme uygulanmaz (bkz. `hasContent`) — `<TBD>` gibi bir değer sessizce düşmez.
   */
  const toBody = (values: NotificationTemplateFormValues): NotificationTemplateUpsert => ({
    operationCodeId: values.operationCodeId as number,
    notificationTypeId: values.notificationTypeId as number,
    subjectTr: values.subjectTr,
    subjectEn: values.subjectEn.trim() === '' ? null : values.subjectEn,
    contentTr: values.contentTr,
    contentEn: hasContent(values.contentEn) ? values.contentEn : null,
    mailTo: values.mailTo.trim() === '' ? null : values.mailTo,
    mailCc: values.mailCc.trim() === '' ? null : values.mailCc,
    mailBcc: values.mailBcc.trim() === '' ? null : values.mailBcc,
  })

  const onSubmit = (values: NotificationTemplateFormValues) => {
    const body = toBody(values)
    const done = (key: string) => {
      toast.show({ severity: 'success', summary: t(key), life: 3000 })
      setEditing(null)
    }
    // Optimistic-lock/generic toast'ı merkezî hata yönetimi (019) basar; ekran yalnız tazeler.
    const onError = (err: unknown) => {
      if (isOptimisticLock(err)) {
        setEditing(null)
        list.refetch()
      }
    }

    if (editing === 'new') {
      create.mutate(body, { onSuccess: () => done('admin.notificationTemplates.toast.created'), onError })
    } else if (editing) {
      update.mutate(
        { id: editing.id, body: { ...body, modDate: editing.modDate } },
        { onSuccess: () => done('admin.notificationTemplates.toast.updated'), onError },
      )
    }
  }

  /**
   * Satır-içi durum toggle'ı — değişiklik anında kalıcıdır (ayrı "Kaydet" yok).
   *
   * Çakışmada (optimistic-lock) liste **kendiliğinden** tazelenir: kullanıcı toast'ın önerdiği tam
   * sayfa yenilemesini yapmadan bir daha deneyebilir (aynı davranış {@link onSubmit}'te de var).
   */
  const onToggle = (row: NotificationTemplate, active: boolean) => {
    setBusyId(row.id)
    changeStatus.mutate(
      { id: row.id, body: { recordStatus: active ? 1 : 0, modDate: row.modDate } },
      {
        onSuccess: () =>
          toast.show({
            severity: 'success',
            summary: t('admin.notificationTemplates.toast.statusChanged'),
            life: 3000,
          }),
        // Toast'ı merkezî hata yönetimi (019) basar; ekran yalnız bayat satırı tazeler.
        onError: (err) => {
          if (isOptimisticLock(err)) list.refetch()
        },
        onSettled: () => setBusyId(null),
      },
    )
  }

  /** Yıkıcı işlem — anayasa gereği ONAY adımı içerir; tek tıkla silinmez. */
  const onDelete = (row: NotificationTemplate) => {
    confirmDialog({
      header: t('admin.notificationTemplates.confirm.deleteTitle'),
      message: t('admin.notificationTemplates.confirm.deleteMessage'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('admin.notificationTemplates.confirm.accept'),
      accept: () => {
        setBusyId(row.id)
        remove.mutate(row.id, {
          onSuccess: () =>
            toast.show({
              severity: 'success',
              summary: t('admin.notificationTemplates.toast.deleted'),
              life: 3000,
            }),
          onSettled: () => setBusyId(null),
        })
      },
    })
  }

  // "Tümü" listenin BAŞINDA, istemcide üretilir; referans kimlikleri koda gömülmez.
  const operationCodeOptions = useMemo(
    () => [
      { label: t('admin.notificationTemplates.filter.operationCodeAll'), value: ALL },
      ...(operationCodes.data ?? []).map((c) => ({ label: c.name, value: c.id })),
    ],
    [operationCodes.data, t],
  )

  const notificationTypeOptions = useMemo(
    () => [
      { label: t('admin.notificationTemplates.filter.notificationTypeAll'), value: ALL },
      ...(notificationTypes.data ?? []).map((n) => ({ label: n.name, value: n.id })),
    ],
    [notificationTypes.data, t],
  )

  const hasFilter = operationCodeId !== ALL || notificationTypeId !== ALL
  const clearAll = () => {
    setOperationCodeId(ALL)
    setNotificationTypeId(ALL)
    setFirst(0)
  }

  return (
    <div className="admin-page">
      <ConfirmDialog />
      <NotificationTemplateFormDialog
        visible={editing != null}
        initial={editing === 'new' ? null : editing}
        operationCodes={operationCodes.data ?? []}
        notificationTypes={notificationTypes.data ?? []}
        parameters={parameters.data ?? []}
        parametersError={parameters.isError}
        submitting={create.isPending || update.isPending}
        onSubmit={onSubmit}
        onHide={() => setEditing(null)}
        readOnly={readOnly}
      />

      <PageHeader
        title={t('nav.admin.notificationTemplates')}
        actions={
          <>
            {/* Yenile, Excel'in SOLUNDA — Figma tablo tasarimi (kullanici istegi 2026-08-26).
                Onceden filtre cubugundaydi (`FilterBar onRefresh`). */}
            <RefreshButton onRefresh={() => list.refetch()} />
            <ExcelExportButton
              onExport={() =>
                exportNotificationTemplates(
                  operationCodeId === ALL ? undefined : operationCodeId,
                  notificationTypeId === ALL ? undefined : notificationTypeId,
                  sorting.sort,
                )
              }
              disabled={items.length === 0}
            />
            {!readOnly && (
              <Button
                icon="pi pi-plus"
                label={t('admin.notificationTemplates.actions.create')}
                onClick={() => setEditing('new')}
              />
            )}
          </>
        }
      />

      <FilterBar
        showClear={hasFilter}
        clearLabel={t('admin.notificationTemplates.filter.clear')}
        onClear={clearAll}
      >
        {/*
          "Tümü" açık bir seçenek olduğundan `showClear` (X) kullanılmaz — aynı işi yapan iki kontrol
          olmaz ve seçim hiçbir zaman boşa düşmez. aria-label kontrolün NE olduğunu söyler; seçili değer
          ise "Tümü"/kod adıdır.
        */}
        <Dropdown
          value={operationCodeId}
          options={operationCodeOptions}
          onChange={(e) => {
            setOperationCodeId((e.value as number | null) ?? ALL)
            setFirst(0)
          }}
          aria-label={t('admin.notificationTemplates.filter.operationCodeLabel')}
          disabled={operationCodes.isError}
          scrollHeight="28rem"
        />
        <Dropdown
          value={notificationTypeId}
          options={notificationTypeOptions}
          onChange={(e) => {
            setNotificationTypeId((e.value as number | null) ?? ALL)
            setFirst(0)
          }}
          aria-label={t('admin.notificationTemplates.filter.notificationTypeLabel')}
          disabled={notificationTypes.isError}
        />
      </FilterBar>

      {/* Sunucu-taraflı sayfalama + kolon sıralaması (lazy → sortField/sortOrder/onSort zorunlu). */}
      {isMobile ? (
        <div className="ui-card-list">
          {list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {!list.isLoading && items.length === 0 && (
            <EmptyState
              title={t('admin.notificationTemplates.empty.title')}
              description={t('admin.notificationTemplates.empty.desc')}
            />
          )}
          {!list.isLoading &&
            items.map((row) => {
              const active = row.recordStatus === 1
              return (
                <div key={row.id} className="ui-card">
                  <div className="ui-card-top">
                    <Badge tone={active ? 'green' : 'neutral'}>
                      {active
                        ? t('admin.notificationTemplates.status.active')
                        : t('admin.notificationTemplates.status.passive')}
                    </Badge>
                    <div className="ui-card-actions ui-card-actions--compact">
                      <InputSwitch
                        className="admin-switch"
                        checked={active}
                        disabled={readOnly || busyId === row.id}
                        onChange={(e) => onToggle(row, Boolean(e.value))}
                        aria-label={row.subjectTr}
                        tooltip={readOnly ? undefined : t('admin.notificationTemplates.actions.toggle')}
                        tooltipOptions={{ position: 'bottom' }}
                      />
                      {/* Salt okunurda menüde tek eylem (Görüntüle) kalırdı → kebab yerine doğrudan düğme. */}
                      {readOnly ? (
                        <Button
                          icon="pi pi-eye"
                          rounded
                          text
                          disabled={loadingRow}
                          aria-label={`${row.subjectTr}, ${t('admin.notificationTemplates.actions.view')}`}
                          onClick={() => openEdit(row)}
                        />
                      ) : (
                        <TemplateCardMenu
                          row={row}
                          busy={busyId === row.id || loadingRow}
                          onEdit={openEdit}
                          onDelete={onDelete}
                          t={t}
                        />
                      )}
                    </div>
                  </div>
                  <div className="ui-card-head">
                    <span className="ui-card-title">{localized(row.subjectTr, row.subjectEn)}</span>
                  </div>
                  <span className="ui-card-sub">
                    {[row.operationCodeName, row.notificationTypeName].filter(Boolean).join(' · ')}
                  </span>
                  {row.mailTo && <span className="ui-card-row">{row.mailTo}</span>}
                </div>
              )
            })}
          {!list.isLoading && total > rows && (
            <Paginator
              first={first}
              rows={rows}
              totalRecords={total}
              onPageChange={(e: PaginatorPageChangeEvent) => {
                setFirst(e.first)
                setRows(e.rows)
              }}
              template="PrevPageLink CurrentPageReport NextPageLink"
              currentPageReportTemplate={t('common.pageReport')}
              className="ui-cards-paginator"
            />
          )}
        </div>
      ) : (
      <DataTable
        value={items}
        loading={list.isLoading}
        lazy
        first={first}
        rows={rows}
        totalRecords={total}
        onPage={(e) => {
          setFirst(e.first)
          setRows(e.rows)
        }}
        sortField={sorting.sortField}
        sortOrder={sorting.sortOrder}
        onSort={sorting.onSort}
        emptyMessage={
          <EmptyState
            title={t('admin.notificationTemplates.empty.title')}
            description={t('admin.notificationTemplates.empty.desc')}
          />
        }
      >
        {notificationTemplateColumns({
          t,
          localized,
          onEdit: openEdit,
          onToggle,
          onDelete,
          busy: loadingRow,
          busyId,
          readOnly,
        })}
      </DataTable>
      )}
    </div>
  )
}
